/*
  Support for IPA
  * https://www.ipachart.com/
  * https://www.internationalphoneticalphabet.org/ipa-charts/ipa-symbols-with-unicode-decimal-and-hex-codes/
  * https://commons.wikimedia.org/wiki/General_phonetics
  * https://en.wikipedia.org/wiki/International_Phonetic_Alphabet
  * https://en.wikipedia.org/wiki/International_Phonetic_Alphabet_chart
*/

import { Feature, toneFeatures } from "./features";
import { symbolsToFeatures } from "./symbolsToFeatures";

export type IpaType = string;

/** get the names of all supported IPA symbols */
export function getAllIpa(): { ipa: IpaType[], diacritics: IpaType[] } {
  return {
    ipa: Object.keys(ipaFeatures),
    diacritics: Object.keys(ipaDiacritics),
  }
}

/** get all IPA that's marked as a modifier (like [ʰ] for aspiration) */
export function getAllModifiers(): IpaType[] {
  const modifiers: IpaType[] = [];
  const ipa = Object.keys(ipaFeatures);
  ipa.forEach(i => { if (ipaFeatures[i][0] === "modifier") modifiers.push(i) });
  return modifiers;
}

/** find the features for a phoneme (which may include diacritics) */
export function ipaToFeatures(ipa: IpaType): Feature[] | undefined {
  const official = symbolsToFeatures(ipa, ipaFeatures, ipaDiacritics);
  return official ?? symbolsToFeatures(ipa, ipaPlusFeatures, {});
}

/** find the features for a single IPA symbol that's not a diacritic */
export function getFeaturesForOne(s: IpaType): Feature[] {
  const features = ipaFeatures[s];
  return features === undefined ? [] : features;
}

/** find the features for a single IPA diacritic, optionally requesting a specific type if present */
export function getIpaDiacriticFeature(s: IpaType, type?: 'V' | 'C' | 'O'): Feature | '' {
  const features = ipaDiacritics[s];
  if (features === undefined) {
    return '';
  }

  if (type !== undefined) {
    if (type === 'V' && features.vowel) {
      return features.vowel;
    } else if (type === 'C' && features.consonant) {
      return features.consonant;
    } else if (type === 'O' && features.other) {
      return features.other;
    }
  }

  return features.vowel ? features.vowel
    : features.consonant ? features.consonant
      : features.other ? features.other
        : '';
}

/** mappings from symbol to the features it represents */
export type SymbolsToFeatures = Record<string, Feature[]>;

/** a symbol that adds to a set of features */
export interface DiacriticFeatures {
  vowel?: Feature;
  consonant?: Feature;
  other?: Feature;
}
/** a set of symbols that each add to a set of features */
export type DiacriticsToFeatures = Record<string, DiacriticFeatures>;

/** tags for categorizing the sounds represented by various IPA symbols */
const ipaFeatures: SymbolsToFeatures = {
  p: ["consonant", "pulmonic", "plosive", "bilabial", "voiceless"],
  b: ["consonant", "pulmonic", "plosive", "bilabial", "voiced"],
  t: ["consonant", "pulmonic", "plosive", "alveolar", "voiceless"],
  d: ["consonant", "pulmonic", "plosive", "alveolar", "voiced"],
  t̠: ["consonant", "pulmonic", "plosive", "postalveolar", "voiceless"], // retracted t is used for the postalveolar plosive
  d̠: ["consonant", "pulmonic", "plosive", "postalveolar", "voiced"], // retracted d is used for the postalveolar plosive
  ʈ: ["consonant", "pulmonic", "plosive", "retroflex", "voiceless"],
  ɖ: ["consonant", "pulmonic", "plosive", "retroflex", "voiced"],
  c: ["consonant", "pulmonic", "plosive", "palatal", "voiceless"],
  ɟ: ["consonant", "pulmonic", "plosive", "palatal", "voiced"],
  k: ["consonant", "pulmonic", "plosive", "velar", "voiceless"],
  g: ["consonant", "pulmonic", "plosive", "velar", "voiced"],
  ɡ: ["consonant", "pulmonic", "plosive", "velar", "voiced"],
  q: ["consonant", "pulmonic", "plosive", "uvular", "voiceless"],
  ɢ: ["consonant", "pulmonic", "plosive", "uvular", "voiced"],
  ʡ: ["consonant", "pulmonic", "plosive", "pharyngeal", "epiglottal", "voiceless"],
  ʔ: ["consonant", "pulmonic", "plosive", "glottal", "voiceless"],
  m: ["consonant", "pulmonic", "nasal", "bilabial", "voiced"],
  ɱ: ["consonant", "pulmonic", "nasal", "labiodental", "voiced"],
  n: ["consonant", "pulmonic", "nasal", "alveolar", "voiced"],
  n̠: ["consonant", "pulmonic", "nasal", "postalveolar", "voiced"], // retracted n is used for the postalveolar nasal
  ɳ: ["consonant", "pulmonic", "nasal", "retroflex", "voiced"],
  ɲ: ["consonant", "pulmonic", "nasal", "palatal", "voiced"],
  ŋ: ["consonant", "pulmonic", "nasal", "velar", "voiced"],
  ɴ: ["consonant", "pulmonic", "nasal", "uvular", "voiced"],
  ʙ: ["consonant", "pulmonic", "trill", "bilabial", "voiced"],
  r: ["consonant", "pulmonic", "trill", "alveolar", "voiced"],
  r̠: ["consonant", "pulmonic", "trill", "postalveolar", "voiced"], // retracted r is used for the postalveolar trill
  ʀ: ["consonant", "pulmonic", "trill", "uvular", "voiced"],
  ᴙ: ["consonant", "pulmonic", "trill", "epiglottal", "voiced"],  // informal usage
  ʜ: ["consonant", "pulmonic", "trill", "pharyngeal", "voiceless"], // or epiglottal trill or epiglottal fricative, different than ħ
  ʢ: ["consonant", "pulmonic", "trill", "pharyngeal", "voiced"], // or epiglottal trill or epiglottal fricative, different than ʕ
  ⱱ̟: ["consonant", "pulmonic", "tap-flap", "bilabial", "voiced"], // advanced used for bilabial
  ⱱ: ["consonant", "pulmonic", "tap-flap", "labiodental", "voiced"],
  ɾ: ["consonant", "pulmonic", "tap-flap", "alveolar", "voiced"],
  ᴅ: ["consonant", "pulmonic", "tap-flap", "alveolar", "voiced"], // ɾ is a tap & ᴅ is a flap
  ɽ: ["consonant", "pulmonic", "tap-flap", "retroflex", "voiced"],
  ɢ̆: ["consonant", "pulmonic", "tap-flap", "uvular", "voiced"],
  ʀ̆: ["consonant", "pulmonic", "tap-flap", "uvular", "voiced"],
  ʡ̆: ["consonant", "pulmonic", "tap-flap", "pharyngeal", "voiced"],
  ɸ: ["consonant", "pulmonic", "fricative", "bilabial", "voiceless"],
  β: ["consonant", "pulmonic", "fricative", "bilabial", "voiced"],
  f: ["consonant", "pulmonic", "fricative", "labiodental", "voiceless"],
  v: ["consonant", "pulmonic", "fricative", "labiodental", "voiced"],
  ʍ: ["consonant", "pulmonic", "fricative", "labial-velar", "voiceless"], // sometimes considered an approximant, a voiceless w
  θ: ["consonant", "pulmonic", "fricative", "dental", "voiceless"],
  ð: ["consonant", "pulmonic", "fricative", "dental", "voiced"],
  s: ["consonant", "pulmonic", "fricative", "alveolar", "voiceless"],
  z: ["consonant", "pulmonic", "fricative", "alveolar", "voiced"],
  ʃ: ["consonant", "pulmonic", "fricative", "postalveolar", "voiceless"],
  ʒ: ["consonant", "pulmonic", "fricative", "postalveolar", "voiced"],
  ʂ: ["consonant", "pulmonic", "fricative", "retroflex", "voiceless"],
  ʐ: ["consonant", "pulmonic", "fricative", "retroflex", "voiced"],
  ɕ: ["consonant", "pulmonic", "fricative", "alveolo-palatal", "voiceless"],
  ʑ: ["consonant", "pulmonic", "fricative", "alveolo-palatal", "voiced"],
  ç: ["consonant", "pulmonic", "fricative", "palatal", "voiceless"],
  ʝ: ["consonant", "pulmonic", "fricative", "palatal", "voiced"],
  x: ["consonant", "pulmonic", "fricative", "velar", "voiceless"],
  ɣ: ["consonant", "pulmonic", "fricative", "velar", "voiced"],
  χ: ["consonant", "pulmonic", "fricative", "uvular", "voiceless"],
  ʁ: ["consonant", "pulmonic", "fricative", "uvular", "voiced"],
  ħ: ["consonant", "pulmonic", "fricative", "pharyngeal", "voiceless"],
  ʕ: ["consonant", "pulmonic", "fricative", "pharyngeal", "voiced"],
  h: ["consonant", "pulmonic", "fricative", "glottal", "voiceless"],
  ɦ: ["consonant", "pulmonic", "fricative", "glottal", "voiced"],
  ɧ: ["consonant", "pulmonic", "fricative", "velar", "ʃ and x", "voiceless"],
  ɬ: ["consonant", "pulmonic", "lateral-fricative", "alveolar", "voiceless"],
  ɮ: ["consonant", "pulmonic", "lateral-fricative", "alveolar", "voiced"],
  ꞎ: ["consonant", "pulmonic", "lateral-fricative", "retroflex", "voiceless"],
  '𝼅': ["consonant", "pulmonic", "lateral-fricative", "retroflex", "voiced"],
  'ɬ̠ʲ': ["consonant", "pulmonic", "lateral-fricative", "alveolo-palatal", "voiceless"], // alveolar retracted palatalized = alveolo-palatal
  '𝼆̟': ["consonant", "pulmonic", "lateral-fricative", "alveolo-palatal", "voiceless"], // palatal advanced = alveolo-palatal
  'ȴ̝̊': ["consonant", "pulmonic", "lateral-fricative", "alveolo-palatal", "voiceless"],  // raised lateral-approximant = lateral-fricative
  '𝼆': ["consonant", "pulmonic", "lateral-fricative", "palatal", "voiceless"],
  'ʎ̥̝': ["consonant", "pulmonic", "lateral-fricative", "palatal", "voiceless"], // raised approximant = fricative
  'ʎ̥': ["consonant", "pulmonic", "lateral-fricative", "palatal", "voiced"], // raised approximant = fricative
  '𝼄': ["consonant", "pulmonic", "lateral-fricative", "velar", "voiceless"],
  ʟ̝: ["consonant", "pulmonic", "lateral-fricative", "velar", "voiced"], // raised approximant = fricative
  ʋ: ["consonant", "pulmonic", "approximant", "labiodental", "voiced"],
  w: ["consonant", "pulmonic", "approximant", "labial-velar", "voiced"],
  ɥ: ["consonant", "pulmonic", "approximant", "labial-palatal", "voiced"],
  ɹ: ["consonant", "pulmonic", "approximant", "alveolar", "voiced"],
  ɻ: ["consonant", "pulmonic", "approximant", "retroflex", "voiced"],
  j: ["consonant", "pulmonic", "approximant", "palatal", "voiced"],
  ɰ: ["consonant", "pulmonic", "approximant", "velar", "voiced"],
  l: ["consonant", "pulmonic", "lateral-approximant", "alveolar", "voiced"],
  ɫ: ["consonant", "pulmonic", "lateral-approximant", "alveolar", "voiced", "velarized"],
  ɭ: ["consonant", "pulmonic", "lateral-approximant", "retroflex", "voiced"],
  ʎ: ["consonant", "pulmonic", "lateral-approximant", "palatal", "voiced"],
  ʟ: ["consonant", "pulmonic", "lateral-approximant", "velar", "voiced"],
  ɺ: ["consonant", "pulmonic", "lateral-flap", "alveolar", "voiced"],
  ʎ̆: ["consonant", "pulmonic", "lateral-flap", "palatal", "voiced"], // short ʎ used for lateral-flap

  ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "voiceless"],
  ǀ: ["consonant", "non-pulmonic", "click", "dental", "voiceless"],
  ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "voiceless"],
  ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "voiceless"],
  '‼': ["consonant", "non-pulmonic", "click", "retroflex", "voiceless"],
  ǂ: ["consonant", "non-pulmonic", "click", "palatal", "voiceless"],
  ʞ: ["consonant", "non-pulmonic", "click", "velar", "voiceless"],
  '𝼋': ["consonant", "non-pulmonic", "click", "palatal", "voiceless", "fricative"],

  k͡ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "velar", "voiceless"],
  k͡ǀ: ["consonant", "non-pulmonic", "click", "dental", "velar", "voiceless"],
  k͡ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "velar", "voiceless"],
  k͡ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "velar", "voiceless"],
  'k͡‼': ["consonant", "non-pulmonic", "click", "retroflex", "velar", "voiceless"],
  k͡ǂ: ["consonant", "non-pulmonic", "click", "palatal", "velar", "voiceless"],
  ɡ͡ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "velar", "voiced"],
  ɡ͡ǀ: ["consonant", "non-pulmonic", "click", "dental", "velar", "voiced"],
  ɡ͡ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "velar", "voiced"],
  ɡ͡ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "velar", "voiced"],
  'ɡ͡‼': ["consonant", "non-pulmonic", "click", "retroflex", "velar", "voiced"],
  ɡ͡ǂ: ["consonant", "non-pulmonic", "click", "palatal", "velar", "voiced"],
  ŋ͡ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "velar", "nasal", "voiced"],
  ŋ͡ǀ: ["consonant", "non-pulmonic", "click", "dental", "velar", "nasal", "voiced"],
  ŋ͡ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "velar", "nasal", "voiced"],
  ŋ͡ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "velar", "nasal", "voiced"],
  'ŋ͡‼': ["consonant", "non-pulmonic", "click", "retroflex", "velar", "nasal", "voiced"],
  ŋ͡ǂ: ["consonant", "non-pulmonic", "click", "palatal", "velar", "nasal", "voiced"],
  q͡ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "uvular", "voiceless"],
  q͡ǀ: ["consonant", "non-pulmonic", "click", "dental", "uvular", "voiceless"],
  q͡ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "uvular", "voiceless"],
  q͡ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "uvular", "voiceless"],
  'q͡‼': ["consonant", "non-pulmonic", "click", "retroflex", "uvular", "voiceless"],
  q͡ǂ: ["consonant", "non-pulmonic", "click", "palatal", "uvular", "voiceless"],
  ɢ͡ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "uvular", "voiced"],
  ɢ͡ǀ: ["consonant", "non-pulmonic", "click", "dental", "uvular", "voiced"],
  ɢ͡ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "uvular", "voiced"],
  ɢ͡ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "uvular", "voiced"],
  'ɢ͡‼': ["consonant", "non-pulmonic", "click", "retroflex", "uvular", "voiced"],
  ɢ͡ǂ: ["consonant", "non-pulmonic", "click", "palatal", "uvular", "voiced"],
  ɴ͡ʘ: ["consonant", "non-pulmonic", "click", "bilabial", "uvular", "nasal", "voiced"],
  ɴ͡ǀ: ["consonant", "non-pulmonic", "click", "dental", "uvular", "nasal", "voiced"],
  ɴ͡ǁ: ["consonant", "non-pulmonic", "click", "lateral-click", "uvular", "nasal", "voiced"],
  ɴ͡ǃ: ["consonant", "non-pulmonic", "click", "alveolar", "uvular", "nasal", "voiced"],
  'ɴ͡‼': ["consonant", "non-pulmonic", "click", "retroflex", "uvular", "nasal", "voiced"],
  ɴ͡ǂ: ["consonant", "non-pulmonic", "click", "palatal", "uvular", "nasal", "voiced"],

  ɓ̥: ["consonant", "non-pulmonic", "implosive", "bilabial", "voiceless"],
  ɓ: ["consonant", "non-pulmonic", "implosive", "bilabial", "voiced"],
  ɗ̥: ["consonant", "non-pulmonic", "implosive", "alveolar", "voiceless"],
  ɗ: ["consonant", "non-pulmonic", "implosive", "alveolar", "voiced"],
  ᶑ: ["consonant", "non-pulmonic", "implosive", "retroflex", "voiced"],
  ʄ̊: ["consonant", "non-pulmonic", "implosive", "palatal", "voiceless"],
  ʄ: ["consonant", "non-pulmonic", "implosive", "palatal", "voiced"],
  ɠ̊: ["consonant", "non-pulmonic", "implosive", "velar", "voiceless"],
  ɠ: ["consonant", "non-pulmonic", "implosive", "velar", "voiced"],
  ʛ̥: ["consonant", "non-pulmonic", "implosive", "uvular", "voiceless"],
  ʛ: ["consonant", "non-pulmonic", "implosive", "uvular", "voiced"],
  // the following are deprecated but are still used, the circle diacritic is preferred now,
  // (the diacritic versions are listed above so they'll be chosen before the deprecated versions)
  ƥ: ["consonant", "non-pulmonic", "implosive", "bilabial", "voiceless"], // ɓ̥
  ƭ: ["consonant", "non-pulmonic", "implosive", "alveolar", "voiceless"], // ɗ̥
  ƈ: ["consonant", "non-pulmonic", "implosive", "palatal", "voiceless"],  // ʄ̊
  ƙ: ["consonant", "non-pulmonic", "implosive", "velar", "voiceless"],    // ɠ̊
  ʠ: ["consonant", "non-pulmonic", "implosive", "uvular", "voiceless"],   // ʛ̥

  p͡ɸ: ["consonant", "pulmonic", "affricate", "bilabial", "voiceless"],
  b͡β: ["consonant", "pulmonic", "affricate", "bilabial", "voiced"],
  p͡f: ["consonant", "pulmonic", "affricate", "labiodental", "voiceless"],
  b͡v: ["consonant", "pulmonic", "affricate", "labiodental", "voiced"],
  t̪͡θ: ["consonant", "pulmonic", "affricate", "dental", "voiceless"],
  d̪͡ð: ["consonant", "pulmonic", "affricate", "dental", "voiced"],
  t͡s: ["consonant", "pulmonic", "affricate", "alveolar", "voiceless"],
  t͡ʃ: ["consonant", "pulmonic", "affricate", "postalveolar", "voiceless"],
  t͡ɕ: ["consonant", "pulmonic", "affricate", "alveolo-palatal", "voiceless"],
  ʈ͡ʂ: ["consonant", "pulmonic", "affricate", "retroflex", "voiceless"],
  d͡z: ["consonant", "pulmonic", "affricate", "alveolar", "voiced"],
  d͡ʒ: ["consonant", "pulmonic", "affricate", "postalveolar", "voiced"],
  d͡ʑ: ["consonant", "pulmonic", "affricate", "alveolo-palatal", "voiced"],
  ɖ͡ʐ: ["consonant", "pulmonic", "affricate", "retroflex", "voiced"],
  c͡ç: ["consonant", "pulmonic", "affricate", "palatal", "voiceless"],
  ɟ͡ʝ: ["consonant", "pulmonic", "affricate", "palatal", "voiced"],
  k͡x: ["consonant", "pulmonic", "affricate", "velar", "voiceless"],
  ɡ͡ɣ: ["consonant", "pulmonic", "affricate", "velar", "voiced"],
  q͡χ: ["consonant", "pulmonic", "affricate", "uvular", "voiceless"],
  ɢ͡ʁ: ["consonant", "pulmonic", "affricate", "uvular", "voiced"],
  ʔ͡h: ["consonant", "pulmonic", "affricate", "glottal", "voiceless"],
  ʡ͡ʜ: ["consonant", "pulmonic", "affricate", "epiglottal", "voiceless"],
  ʡ͡ʢ: ["consonant", "pulmonic", "affricate", "epiglottal", "voiced"],
  t͡ɬ: ["consonant", "pulmonic", "lateral-affricate", "alveolar", "voiceless"],
  d͡ɮ: ["consonant", "pulmonic", "lateral-affricate", "alveolar", "voiced"],
  t͡ꞎ: ["consonant", "pulmonic", "lateral-affricate", "retroflex", "voiceless"],
  ɟ͡ʎ̝: ["consonant", "pulmonic", "lateral-affricate", "palatal", "voiced"],
  'k͡𝼄': ["consonant", "pulmonic", "lateral-affricate", "velar", "voiceless"],
  ɡ͡ʟ̝: ["consonant", "pulmonic", "lateral-affricate", "velar", "voiced"],
  // no tie
  t̪θ: ["consonant", "pulmonic", "affricate", "dental", "voiceless"],
  d̪ð: ["consonant", "pulmonic", "affricate", "dental", "voiced"],
  ts: ["consonant", "pulmonic", "affricate", "alveolar", "voiceless"],
  t̠ʃ: ["consonant", "pulmonic", "affricate", "postalveolar", "voiceless"],
  tʃ: ["consonant", "pulmonic", "affricate", "postalveolar", "voiceless"],
  tɕ: ["consonant", "pulmonic", "affricate", "alveolo-palatal", "voiceless"],
  ʈʂ: ["consonant", "pulmonic", "affricate", "retroflex", "voiceless"],
  dz: ["consonant", "pulmonic", "affricate", "alveolar", "voiced"],
  d̠ʒ: ["consonant", "pulmonic", "affricate", "postalveolar", "voiced"],
  dʒ: ["consonant", "pulmonic", "affricate", "postalveolar", "voiced"],
  dʑ: ["consonant", "pulmonic", "affricate", "alveolo-palatal", "voiced"],
  ɖʐ: ["consonant", "pulmonic", "affricate", "retroflex", "voiced"],
  cç: ["consonant", "pulmonic", "affricate", "palatal", "voiceless"],
  ɟʝ: ["consonant", "pulmonic", "affricate", "palatal", "voiced"],

  // co-articulated consonants
  n͡m: ["consonant", "pulmonic", "nasal", "labial-alveolar", "voiced"],
  ɳ͡m: ["consonant", "pulmonic", "nasal", "labial-retroflex", "voiced"],
  ŋ͡m: ["consonant", "pulmonic", "nasal", "labial-velar", "voiced"],
  t͡p: ["consonant", "pulmonic", "plosive", "labial-alveolar", "voiceless"],
  d͡b: ["consonant", "pulmonic", "plosive", "labial-alveolar", "voiced"],
  ʈ͡p: ["consonant", "pulmonic", "plosive", "labial-retroflex", "voiceless"],
  ɖ͡b: ["consonant", "pulmonic", "plosive", "labial-retroflex", "voiced"],
  k͡p: ["consonant", "pulmonic", "plosive", "labial-velar", "voiceless"],
  ɡ͡b: ["consonant", "pulmonic", "plosive", "labial-velar", "voiced"],
  q͡ʡ: ["consonant", "pulmonic", "plosive", "uvular-epiglottal", "voiceless"],
  q͡p: ["consonant", "pulmonic", "plosive", "labial-uvular", "voiceless"],
  ɠ̊͡ɓ̥: ["consonant", "non-pulmonic", "implosive", "labial-velar", "voiceless"],
  ɠ͡ɓ: ["consonant", "non-pulmonic", "implosive", "labial-velar", "voiced"],

  // deprecated IPA (though phoible still uses these)
  ȶ: ["consonant", "pulmonic", "plosive", "alveolo-palatal", "voiceless"],   // \u0236 /t̠ʲ/
  ȡ: ["consonant", "pulmonic", "plosive", "alveolo-palatal", "voiced"],      // \u0221 /d̠ʲ/
  ȵ: ["consonant", "pulmonic", "nasal", "alveolo-palatal", "voiced"],        // \u0235 [n̠ʲ]/[ɲ̟]
  ʆ: ["consonant", "pulmonic", "fricative", "alveolo-palatal", "voiceless"], // \u286 /ʃʲ/ or /ɕ/ palatalized voiceless postalveolar sibilant fricative
  ʓ: ["consonant", "pulmonic", "fricative", "alveolo-palatal", "voiced"],    // \u293 /ʒʲ/ or /ʑ/ palatalized voiced postalveolar sibilant fricative
  ȴ: ["consonant", "pulmonic", "lateral-approximant", "alveolo-palatal", "voiced"],  // \u0234 [l̠ʲ]/[ʎ̟] alveopalatal liquid

  i: ["vowel", "close", "front", "unrounded"],
  y: ["vowel", "close", "front", "rounded"],
  ɨ: ["vowel", "close", "central", "unrounded"],
  ʉ: ["vowel", "close", "central", "rounded"],
  ɯ: ["vowel", "close", "back", "unrounded"],
  u: ["vowel", "close", "back", "rounded"],
  ɪ: ["vowel", "near-close", "near-front", "unrounded"],
  ʏ: ["vowel", "near-close", "near-front", "rounded"],
  ʊ: ["vowel", "near-close", "near-back", "rounded"],
  e: ["vowel", "close-mid", "front", "unrounded"],
  ø: ["vowel", "close-mid", "front", "rounded"],
  ɘ: ["vowel", "close-mid", "central", "unrounded"],
  ɵ: ["vowel", "close-mid", "central", "rounded"],
  ɤ: ["vowel", "close-mid", "back", "unrounded"],
  o: ["vowel", "close-mid", "back", "rounded"],
  ə: ["vowel", "mid", "central", "unrounded"],
  ɛ: ["vowel", "open-mid", "front", "unrounded"],
  œ: ["vowel", "open-mid", "front", "rounded"],
  ɜ: ["vowel", "open-mid", "central", "unrounded"],
  ɞ: ["vowel", "open-mid", "central", "rounded"],
  ʌ: ["vowel", "open-mid", "back", "unrounded"],
  ɔ: ["vowel", "open-mid", "back", "rounded"],
  æ: ["vowel", "near-open", "front", "unrounded"],
  ɐ: ["vowel", "near-open", "central", "unrounded"],
  a: ["vowel", "open", "front", "unrounded"],
  ɶ: ["vowel", "open", "front", "rounded"],
  ɑ: ["vowel", "open", "back", "unrounded"],
  ɒ: ["vowel", "open", "back", "rounded"],
  ɝ: ["vowel", "open-mid", "central", "rhotacized"],
  ɚ: ["vowel", "mid", "central", "rhotacized"],
  ᵊ: ["vowel", "mid", "central", "epenthetic"], // ə

  // modifiers
  ᵝ: ["modifier", "labial-spreading"],
  ʰ: ["modifier", "aspirated"],
  ʱ: ["modifier", "breathy-voiced-aspirated"],
  ᶣ: ["modifier", "labio-palatalization"],
  ˠ: ["modifier", "velarized"],
  ʲ: ["modifier", "palatalized"],
  ˡ: ["modifier", "lateral-release"],
  ⁿ: ["modifier", "nasalized"],
  ʳ: ["modifier", "rhotacized"],
  ʴ: ["modifier", "rhotacized"],
  ᶹ: ["modifier", "labio-dentalization"],
  ʷ: ["modifier", "labialized"],
  ˤ: ["modifier", "pharyngealized"],
  ˁ: ["modifier", "pharyngealized"],
  ˀ: ["modifier", "glottalized"],

  // prosody
  'ˈ': ["prosody", "primary-stress"],
  'ˌ': ["prosody", "secondary-stress"],
  '-': ["prosody", "morpheme-break"],
  '.': ["prosody", "syllable-break"],
  '|': ["prosody", "minor-break"], ',': ["prosody", "minor-break"],
  '‖': ["prosody", "major-break"],
  '\u203F': ["prosody", "no-break"],    // ‿ linking
  '\u2040': ["prosody", "no-break"],    // ⁀
  // basic tone symbols, which can be combined into more complex tones
  '˩': ["prosody", "tone", "extra-low-tone"],
  '˨': ["prosody", "tone", "low-tone"],
  '˧': ["prosody", "tone", "mid-tone"],
  '˦': ["prosody", "tone", "high-tone"],
  '˥': ["prosody", "tone", "extra-high-tone"],
  // steps
  '\ua71b': ["prosody", "upstep"],         // ꜛ
  '\ua71c': ["prosody", "downstep"],       // ꜜ
  // intonation
  '↗': ["prosody", "global-rise"],
  '↘': ["prosody", "global-fall"],

  // grouping
  '/': ["group", "delimit", "abstract-phonemes"],
  '[': ["group", "delimit", "detailed-phonemes", "start"],
  ']': ["group", "delimit", "detailed-phonemes", "end"],
  '⟦': ["group", "delimit", "precise-phonemes", "start"],
  '⟧': ["group", "delimit", "precise-phonemes", "end"],
  '{': ["group", "delimit", "prosodic-notation", "start"],
  '}': ["group", "delimit", "prosodic-notation", "end"],
  '(': ["group", "delimit", "indistinguishable-utterance", "start"],
  ')': ["group", "delimit", "indistinguishable-utterance", "end"],
  '⸨': ["group", "delimit", "obscuring-noise", "start"],
  '⸩': ["group", "delimit", "obscuring-noise", "end"],
  '⫽': ["group", "delimit", "morphophonemic-transcription"],
  '⟨': ["group", "delimit", "orthography", "start"],
  '⟩': ["group", "delimit", "orthography", "end"],
  '⟪': ["group", "delimit", "orthography", "start"],
  '⟫': ["group", "delimit", "orthography", "end"],
  '~': ["group", "choice"],   // e.g., f~v
  '\u035C': ["group", "combine"],                  // tie-below, e.g., x͜x
  '\u0361': ["group", "combine"],                  // tie-above, e.g., x͡x
  '\u200C': ["group", "nocombine"],                // zero-width non-joiner
  '\u200D': ["group", "combine"],                  // zero-width joiner
};

/** characters put between two other symbols to combine them, with the first one being the default */
export const ipaCombiners = ['\u0361', '\u035C', '\u200D'];

/**
 * IPA with diacritics that may appear in a feature chart.
 * NOTE that their features can easily be derived,
 * so they don't need to be included in ipaFeatures
 */
export const chartMultiIpa: IpaType[] = [
  // dentals
  't̪', 'd̪', 'n̪', 'r̪', 'ɾ̪', 'ɬ̪', 'ɮ̪', 'l̪',
  // voiceless
  'm̥', 'ɱ̥', 'n̥', 'ɳ̥', 'ɲ̥', 'ŋ̥', 'r̥', 'ʀ̥', 'ɾ̥', 'ɽ̊', 'l̥', 'ɓ̥', 'ɗ̥', 'ʄ̥', 'ɠ̥', 'ʛ̥',
  // lowered fricatives
  'β̞', 'ð̞', 'ʁ̞',
  // ejectives
  'pʼ', 't̪ʼ', 'tʼ', 'ʈʼ', 'cʼ', 'kʼ', 'qʼ',
  'ɸʼ', 'fʼ', 'θʼ', 'ðʼ', 'wʼ', 'sʼ', 'zʼ', 'ʃʼ', 'ʒʼ', 'ʂʼ', 'çʼ', 'ɕʼ', 'xʼ', 'χʼ', 'ʕʼ', 'hʼ',
  // lowered vowels
  'ʊ̞', 'e̞', 'ø̞', 'ɤ̞', 'o̞',
];

/** used in 'ipaPlus', non-standard extensions to IPA */
const ipaPlusFeatures: SymbolsToFeatures = {
  '0': ['length'], '1': ['length'], '2': ['length'], '3': ['length'], '4': ['length'],
  '5': ['length'], '6': ['length'], '7': ['length'], '8': ['length'], '9': ['length'],
};

const ipaDiacritics: DiacriticsToFeatures = {
  '\u02BC': { consonant: 'ejective' },             // ʼ
  '\u02D0': { other: 'long' },                     // ː
  '\u02D1': { other: 'half-long' },                // ˑ
  '\u02D2': { other: 'more-rounded' },             // e.g., ◌˒ (like \u0339)
  '\u02D3': { other: 'less-rounded' },             // e.g., ◌˓ (like \u031C)
  '\u02D4': { other: 'raised' },                   // ɣ˔ (like \u031D)
  '\u02D5': { other: 'lowered' },                  // ð˕ (like \u031E)
  '\u02D6': { other: 'advanced' },                 // ◌˖ (like \u031F)
  '\u02D7': { other: 'retracted' },                // ◌˗ (like \u0320)
  '\u02DA': { other: 'unexploded' },               // ˚
  '\u02DE': { other: 'rhotacized' },               // ˞ 
  '\u02ED': { consonant: 'tenuis' },               // ˭ tenuis: voiceless, unaspirated, unglottalized
  '\u02F9': { other: 'audibly-released' },         // e.g., ◌˹
  '\u0303': { vowel: 'nasalized' },                // e.g., ẽ
  '\u0306': { vowel: 'extra-short' },              // e.g., ĕ
  '\u0308': { other: 'centralized' },              // e.g., ë ẅ
  '\u030A': { consonant: 'voiceless' },            // e.g., ŋ̊
  '\u030D': { consonant: 'syllabic' },             // e.g., ŋ̍ (same as \u0329, but with descenders)
  '\u0318': { vowel: 'advanced-tongue-root' },     // e.g., e̘
  '\u0319': { vowel: 'retracted-tongue-root' },    // e.g., e̙
  '\u0311': { vowel: 'non-syllabic' },             // e.g., ◌̑  (like \u032F)
  '\u031A': { other: 'not-audibly-released' },     // e.g., t̚
  '\u031C': { other: 'less-rounded' },             // e.g., ɔ̜
  '\u031D': { other: 'raised' },                   // e.g., e̝ ɹ̝
  '\u031E': { other: 'lowered' },                  // e.g., e̞ β̞  (like \u02D5)
  '\u031F': { other: 'advanced' },                 // e.g., u̟ ɡ̟
  '\u0320': { other: 'retracted' },                // e.g., e̠
  '\u0321': { other: 'palatalized' },              // ̡
  '\u0322': { consonant: 'retroflex' },            // ̢
  '\u0323': { consonant: 'retroflex' },            // ̣
  '\u0324': { other: 'breathy-voiced' },           // e.g., b̤ a̤
  '\u0325': { other: 'voiceless' },                // e.g., n̥ d̥
  '\u0329': { consonant: 'syllabic' },             // e.g., m̩ n̩ l̩
  '\u032A': { consonant: 'dental' },               // e.g., t̪ d̪
  '\u032C': { other: 'voiced' },                   // ̬ 
  '\u032F': { vowel: 'non-syllabic' },             // e.g., e̯
  '\u0330': { other: 'creaky-voiced' },            // e.g., b̰ a̰
  '\u0339': { other: 'more-rounded' },             // e.g., ɔ̹
  '\u033C': { consonant: 'linguolabial' },         // e.g., s̬ t̬
  '\u0334': { consonant: 'velarized' },            // e.g., l̴  n̴ , or pharyngealized
  '\u033A': { other: 'apical' },                   // e.g., t̺ d̺
  '\u033B': { other: 'laminal' },                  // e.g., t̻ d̻
  '\u033D': { vowel: 'mid-centralized' },          // e.g., e̽
  '\u0346': { consonant: 'labiodental' },          // ͆
  '\u0347': { consonant: 'alveolar' },             // ͇
  '\u0348': { consonant: 'faucalized' },           // ͈ 
  '\u0349': { consonant: 'weak-articulation' },    // ͉ 
  '\u034D': { other: 'labial-spreading' },         // e.g., ɔ͍, or "spread-lip"
  '\u034E': { other: 'whistled' },                 // e.g., s͎ z͎
  '\u0351': { other: 'less-rounded' },             // e.g., ◌͑  (like \u031C)
  '\u0353': { consonant: 'uralic' },               // ͓ 
  '\u0357': { other: 'more-rounded' },             // e.g., ◌͗  (like \u0339)
  '\u2191': { other: 'egressive-airflow' },        // ↑
  '\u2193': { other: 'ingressive-airflow' },       // ↓
  '\uA7f8': { vowel: 'faucalized' },               // ꟸ
  '\uAB6A': { vowel: 'advanced-tongue-root' },     // e.g., ◌꭪ (like \u0318)
  '\uAB6B': { vowel: 'retracted-tongue-root' },    // e.g., ◌꭫ (like \u0319)

  // tones
  '\u030F': { vowel: 'extra-low-tone' },           // e.g., ȅ  ˩
  '\u0300': { vowel: 'low-tone' },                 // e.g., è  ˨
  '\u0304': { vowel: 'mid-tone' },                 // e.g., ē  ˧
  '\u0301': { vowel: 'high-tone' },                // e.g., é  ˦
  '\u030B': { vowel: 'extra-high-tone' },          // e.g., e̋  ˥
  '\u030c': { other: 'rising' },         // ě (looks like \u02c7)
  '\u0302': { vowel: 'falling', consonant: 'alveolo-palatal' },  // ê t̂
  '\u1dc4': { other: 'high-rising' },    // e᷄   ˧˥
  '\u1dc5': { other: 'low-rising' },     // e᷅   ˩˧
  '\u1dc6': { other: 'low-falling' },    // ◌᷆   ˧˩
  '\u1dc7': { other: 'high-falling' },   // ◌᷇   ˥˧
  '\u1dc8': { other: 'rising-falling' }, // e᷈   ˩˥˩
  '\u1dc9': { other: 'falling-rising' }, // ◌᷉   ˥˩˥

  // PIE, e.g. h₁
  '₁': { consonant: 'laryngeal' },
  '₂': { consonant: 'laryngeal' },
  '₃': { consonant: 'laryngeal' },
  // ₐ ₑ ₒ ₓ ₔ used for PIE laryngeal vowel-coloring
  'ₐ': { other: 'vowel-coloring' },
  'ₑ': { other: 'vowel-coloring' },
  'ₒ': { other: 'vowel-coloring' },
  'ₓ': { other: 'vowel-coloring' },
  'ₔ': { other: 'vowel-coloring' },
}

/** diacritics that mark IPA as non-syllabic, with the first one being the default */
export const ipaNonSyllabic = ['\u032F', '\u0311'];

export const nullSymbol = '∅';
export const dottedCircle = '◌';  // used to help illustrated combining characters
export const soundChange = '→';
export const soundChangeRTL = '←';

export const syllableBreak = '.';  // a period
export const morphemeBreak = '-';  // a dash

export const moraBoundary = 'μ';
export const syllableBoundary = 'σ';  // or $
export const morphemeBoundary = '+';
export const wordBoundary = '#';
export const phraseBoundary = '$';
export const prosodicBoundary = '%';

export const primaryStress = '\u02C8';    // ˈ
export const secondaryStress = '\u02CC';  // ˌ

export const ipaStress: string[] = [
  primaryStress,
  secondaryStress,
];

export function isStress(ipa: IpaType): boolean {
  return ipa === primaryStress || ipa === secondaryStress;
}

// is this one of the prosody tones?: ˥ ˦ ˧ ˨ ˩
export function isTone(ipa: IpaType): boolean {
  const code = ipa.charCodeAt(0);
  return 0x2e5 <= code && code <= 0x2e9;
}

/** does this phoneme have any tone features? */
export function hasToneFeature(ipa: IpaType): boolean {
  const feats = ipaToFeatures(ipa);
  if (feats === undefined) {
    return false;
  } else if (feats.includes('tone')) {
    return true;
  }
  // check all of the diacritic tone features
  for (const f of toneFeatures) {
    if (feats.includes(f)) {
      return true;
    }
  }
  return false;
}
